import { PartnerSettingValue } from '@provision/entity-types';
import { portalPaths } from '@provision/portal-routes';
import { useMemo } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { isSettingsEnabled, useGroupRules, usePartnerSettings } from 'src/graphql';
import {
  CustomViewEntity,
  EntityRulesData,
  RULES_MAP_PER_VIEW_ENTITY,
} from 'src/graphql/entities/custom-view';
import { EntityNames } from 'src/graphql/types';
import {
  CUSTOM_VIEW_ENTITY_LABELS,
  MAP_ENTITY_TO_URL_ENTITY,
  PLATFORM_PARTNER_HIDDEN_VIEW_TABS,
} from 'src/pages/CustomViews/constants';
import { useGlobalStore } from 'src/store';
import { trimDuplicatedSlashes } from 'src/utils/routing/use-navigate-route';

const TABS: ({
  label: string;
  entity: CustomViewEntity;
  enabled?: boolean;
} & EntityRulesData)[] = [
  {
    label: CUSTOM_VIEW_ENTITY_LABELS[EntityNames.Conversation],
    entity: EntityNames.Conversation as const,
    enabled: true,
  },
  {
    label: CUSTOM_VIEW_ENTITY_LABELS[EntityNames.Site],
    entity: EntityNames.Site as const,
    enabled: true,
  },
  {
    label: CUSTOM_VIEW_ENTITY_LABELS[EntityNames.Client],
    entity: EntityNames.Client as const,
    enabled: true,
  },
  {
    label: CUSTOM_VIEW_ENTITY_LABELS[EntityNames.Project],
    entity: EntityNames.Project as const,
    enabled: true,
  },
].map((i) => ({
  ...i,
  ...RULES_MAP_PER_VIEW_ENTITY[i.entity],
}));

const isPlatformPartnerManageableTab = (
  entity: CustomViewEntity,
  selectedPartnerId: number | null,
): boolean =>
  selectedPartnerId === PLATFORM_PARTNER_ID &&
  (entity === EntityNames.Conversation || entity === EntityNames.Site);

export const useCustomViewTabs = () => {
  const { checkValidity, loading: rulesLoading } = useGroupRules();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { data: partnerSettings, loading: loadingSettings } = usePartnerSettings({
    partnerId: selectedPartnerId,
  });

  const loading = rulesLoading || loadingSettings;

  const tabs = useMemo(() => {
    if (loading) return [];

    return TABS.filter((item) => {
      if (!item.enabled) {
        return false;
      }

      if (
        selectedPartnerId === PLATFORM_PARTNER_ID &&
        PLATFORM_PARTNER_HIDDEN_VIEW_TABS.includes(item.entity)
      ) {
        return false;
      }

      const rulesMatch =
        isPlatformPartnerManageableTab(item.entity, selectedPartnerId) ||
        !item.allowedRules ||
        checkValidity(item.allowedRules);

      if (!rulesMatch) {
        return false;
      }

      const settingsDisallowMatch =
        item.disallowedPartnerSettings?.some((settingId) =>
          partnerSettings.some((data) =>
            isSettingsEnabled(settingId, data as PartnerSettingValue),
          ),
        ) ?? false;

      return !settingsDisallowMatch;
    });
  }, [checkValidity, partnerSettings, loading, selectedPartnerId]);

  return { tabs, loading };
};

export const getFirstAccessibleTabPath = (
  tabs: { entity: CustomViewEntity }[],
  partnerPrefix?: string | null,
): string => {
  const fallbackEntity = tabs[0]?.entity;
  const path = fallbackEntity
    ? portalPaths.customView.view(MAP_ENTITY_TO_URL_ENTITY[fallbackEntity] ?? fallbackEntity)
    : portalPaths.root();
  return trimDuplicatedSlashes(partnerPrefix ? `/${partnerPrefix}/${path}` : path);
};
